import { useState, useCallback, useRef } from 'react';
import { AIMessage, AIQueryRequest, AIQueryResponse } from '../types/ai.types';
import { useUser } from '../../../components/UserContext';
import { createApiClient } from '../../../components/useApiClient';
import { useCurrency } from '../../../components/CurrencyContext';

export const useCollectionChat = () => {
    const { currency } = useCurrency();

    const [messages, setMessages] = useState<AIMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([
        'What is my total collection value?',
        'Which works haven\'t been valued in 2 years?',
        'Show all works currently on loan',
        'Which artists have the highest concentration?',
        'Show me potential insurance gaps',
    ]);

    const { getAccessToken, activeClientId } = useUser();
    const clientIdRef = useRef(activeClientId);
    clientIdRef.current = activeClientId;

    const api = createApiClient(
        { getAccessToken, getClientId: () => clientIdRef.current },
        { timeoutMs: 30000 }
    );

    const sendMessage = useCallback(async (question: string) => {
        if (!question.trim() || isLoading) return;

        const userMsg: AIMessage = {
            id: crypto.randomUUID(),
            role: 'user',
            content: question,
            timestamp: new Date(),
        };

        const streamingMsg: AIMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isStreaming: true,
        };

        setMessages(prev => [...prev, userMsg, streamingMsg]);
        setIsLoading(true);

        try {
            const history = messages.map(m => ({
                role: m.role,
                content: m.content,
            }));

            const request: AIQueryRequest = {
                question,
                conversationHistory: history,
                baseCurrency: currency,
            };

            const accessToken = await getAccessToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/ai/query`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(request),
                }
            );

            if (!response.ok) throw new Error('AI query failed');

            const data: AIQueryResponse = await response.json();

            setMessages(prev =>
                prev.map(m =>
                    m.id === streamingMsg.id
                        ? {
                            ...m,
                            content: data.answer,
                            isStreaming: false,
                            result: {
                                visualizationType: data.visualizationType,
                                data: data.data,
                                columns: data.columns,
                                chartConfig: data.chartConfig,
                            },
                        }
                        : m
                )
            );

            if (data.suggestedFollowUps?.length) {
                setSuggestions(data.suggestedFollowUps);
            }
        } catch {
            setMessages(prev =>
                prev.map(m =>
                    m.id === streamingMsg.id
                        ? {
                            ...m,
                            content: 'Sorry, I couldn\'t process that request. Please try again.',
                            isStreaming: false,
                        }
                        : m
                )
            );
        } finally {
            setIsLoading(false);
        }
    }, [messages, isLoading, getAccessToken, currency]);

    const clearConversation = useCallback(() => {
        setMessages([]);
    }, []);

    return { messages, isLoading, suggestions, sendMessage, clearConversation };
};